import React, { useContext, useEffect, useState } from "react";

import request from "app/http";
import _ from "app/lang";
import { Cloud, User } from "app/models";
import { pageContext, PartialContext } from "app/page/ContextProvider";

import DrawerMenu, { DrawerMenuItem } from "./DrawerMenu";

export function getMenuItems(context: PartialContext) {
    if (!context.cloud) {
        return [];
    }

    const cloudId = context.cloud.id;

    const [hasSNVRLicense, setHasSNVRLicense] = useState(false);

    useEffect(
        () => {
            request(`/apiv2/cloud/${cloudId}/addon/27/get-specific-addon-license`)
            .then(res => {
                setHasSNVRLicense(res.count > 0);
            });
        },
        []
    );

    const cloud = new Cloud(context.cloud);
    const user = new User(context);
    const baseUrl = "/cloud/:cloudId";

    const items: DrawerMenuItem[] = [
        {
            icon: "dashboard",
            text: _("Dashboard"),
            href: `${baseUrl}/dashboard`
        },
        {
            icon: "ignite_ap",
            text: _("Devices"),
            href: "/v2/cloud/:cloudId/devices"
        }
    ];

    if (hasSNVRLicense && user.hasPermission("manage_snvr")) {
        items.push({
            icon: "smartnvr",
            text: _("Smart NVRs"),
            href: "/v2/cloud/:cloudId/smartnvrs"
        });
    }

    items.push({
        icon: "list_alt",
        text: _("Activity"),
        href: `${baseUrl}/activity`
    });


    const managementItems: DrawerMenuItem[] = [];

    if (user.hasPermission("manage_sites")) {
        managementItems.push({
            icon: "business",
            text: _("Site management"),
            href: "/v2/cloud/:cloudId/sites"
        });
    } else {
        items.push({
            icon: "business",
            text: _("Sites"),
            href: "/v2/cloud/:cloudId/sites"
        });
    }

    if (user.hasPermission("manage_users")) {
        managementItems.push({
            icon: "users",
            text: _("User management"),
            href: "/v2/cloud/:cloudId/users"
        });
    }

    if (user.hasPermission("manage_sites")) {
        managementItems.push({
            icon: "report",
            text: _("Report management"),
            href: "/v2/cloud/:cloudId/report"
        });
    }

    if (user.support_tools) {
        managementItems.push({
            icon: "build",
            text: _("Tools"),
            href: "/v2/cloud/:cloudId/tools"
        });
    }

    if (user.hasPermission("manage_licenses") && context.theme?.turnOnAddon) {
        managementItems.push(
            {
                icon: "developer_board",
                text: _("Add-ons"),
                href: `${baseUrl}/addons`
            },
            {
                icon: "money",
                text: _("Licenses & Billing"),
                href: `${baseUrl}/licenses`
            }
        );

    }

    if (user.hasPermission("manage_vpc") && cloud.planType === "vpc") {
        managementItems.push(
            {
                icon: "cloud",
                text: _("VPC"),
                href: `${baseUrl}/vpc`
            }
        );
    }

    // Not sure this is the right permission here? Copied over from PHP code.
    if (user.hasPermission("manage_licenses")) {
        managementItems.push(
            {
                icon: "edit_attributes",
                text: _(" Properties"),
                href: "/v2/cloud/:cloudId/edit"
            }
        );
    }

    if (user.hasPermission("manage_sites") && context.cloud.authport_enabled) {
        managementItems.push(
            {
                icon: "wifi_lock",
                text: _("AuthPort"),
                subitems: [
                    {
                        icon: "money",
                        text: _("Service Plans"),
                        href: "/v2/cloud/:cloudId/authport/service-plans"
                    },
                    {
                        icon: "account_box",
                        text: _("Accounts"),
                        href: "/v2/cloud/:cloudId/authport/accounts"
                    },
                    {
                        icon: "lock",
                        text: _("Certificate"),
                        href: "/v2/cloud/:cloudId/authport/certificate"
                    },
                    {
                        icon: "edit",
                        text: _("Captive Portal"),
                        href: "/v2/cloud/:cloudId/authport/captive-portals"
                    },
                    {
                        icon: "list",
                        text: _("Event Log"),
                        href: "/v2/cloud/:cloudId/authport/history"
                    },
                ]
            }
        );
    }

    if (user.hasPermission("manage_sites")) {
        managementItems.push(
            {
                icon: "notifications",
                text: _("Notifications"),
                href: "/v2/cloud/:cloudId/notification-settings"
            }
        );
    }

    if (managementItems.length > 0) {
        items.push({ text: _("Manage") });
        items.push.apply(items, managementItems);
    }

    return items;
}

export default function CloudMenu() {
    const items = getMenuItems(useContext(pageContext));

    return <DrawerMenu items={items} />;
}
